import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

function BusinessWallet() {
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Add Funds State
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositAmount, setDepositAmount] = useState('');
    const [depositing, setDepositing] = useState(false);

    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchWallet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchWallet = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/business/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWallet(res.data);
        } catch (err) {
            console.error('Failed to load wallet:', err);
            // Show a fallback so the UI doesn't crash
            setWallet({ balance: 0, escrowBalance: 0, recentLedgerEntries: [] });
        }
        setLoading(false);
    };

    const handleDeposit = async (e) => {
        e.preventDefault();
        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        setDepositing(true);
        try {
            // Use the response from the deposit to update the UI directly
            const res = await axios.post(
                `${API_URL}/api/business/wallet/deposit`,
                { amount: parseFloat(depositAmount) },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Update the wallet state with the fresh data from the response
            setWallet(res.data);
            
            toast.success(`Successfully deposited $${depositAmount}!`);
            setShowDepositModal(false);
            setDepositAmount('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to deposit funds.');
        }
        setDepositing(false);
    };

    if (loading) {
        return <div className="card" style={{ padding: '24px', color: 'var(--text-muted)' }}>Loading wallet...</div>;
    }

    if (!wallet) return null;

    // Support both `recentTransactions` (old) and `recentLedgerEntries` (new)
    const transactions = wallet.recentLedgerEntries || wallet.recentTransactions || [];

    return (
        <>
            <div className="card card-hover" style={{ padding: '24px', marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '18px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🏢 Business Wallet
                    </h3>
                    <span className="badge">USD</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border)', padding: '16px', borderRadius: 'var(--radius-lg)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Available Balance</span>
                        <span style={{ fontSize: '28px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.03em' }}>
                            ${parseFloat(wallet.balance || 0).toFixed(2)}
                        </span>
                    </div>

                    <div style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '16px', borderRadius: 'var(--radius-lg)' }}>
                        <span style={{ fontSize: '13px', color: 'var(--warning)', display: 'block', marginBottom: '8px' }}>Locked in Escrow</span>
                        <span style={{ fontSize: '28px', fontWeight: '600', color: 'var(--warning)', letterSpacing: '-0.03em' }}>
                            ${parseFloat(wallet.escrowBalance || 0).toFixed(2)}
                        </span>
                    </div>
                </div>

                {/* Recent Ledger Entries */}
                {transactions.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '12px' }}>Recent Transactions</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {transactions.slice(0, 5).map((tx, idx) => (
                                <li key={tx.id || idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                                    <div>
                                        <span style={{ fontWeight: '500', fontSize: '14px', color: tx.amount >= 0 ? 'var(--success)' : 'var(--error)', marginRight: '12px' }}>
                                            {tx.amount >= 0 ? '+' : ''}${parseFloat(tx.amount || 0).toFixed(2)}
                                        </span>
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{tx.description || tx.type}</span>
                                    </div>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                        {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => setShowDepositModal(true)} className="btn-primary">
                        + Add Funds
                    </button>
                    <Link to="/business/spending" className="btn-secondary" style={{ padding: '10px 16px', fontSize: '13px' }}>
                        View Spending →
                    </Link>
                </div>
            </div>

            {/* ADD FUNDS MODAL */}
            {showDepositModal && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={() => setShowDepositModal(false)}>
                    <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: 'var(--text)' }}>Add Funds to Wallet</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            This simulates a Stripe checkout. The funds will be instantly added to your available balance.
                        </p>
                        
                        <div className="form-field">
                            <label className="form-label">Amount (USD)</label>
                            <input
                                type="number"
                                placeholder="e.g. 500"
                                value={depositAmount}
                                onChange={(e) => setDepositAmount(e.target.value)}
                                min="1"
                                step="0.01"
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button onClick={() => setShowDepositModal(false)} className="btn-secondary" disabled={depositing}>
                                Cancel
                            </button>
                            <button onClick={handleDeposit} className="btn-primary" disabled={depositing}>
                                {depositing ? 'Processing...' : 'Simulate Deposit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default BusinessWallet;